import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { 
  Play, Music, FileText, Gauge, Star, CheckCircle, 
  Crown, ChevronRight, Users, BookOpen, Award, Sparkles,
  ChevronLeft as ChevronLeftIcon, Clock, ArrowRight, Mail, Send, RefreshCw
} from 'lucide-react';
import { MainNavigation } from '@/components/MainNavigation';
import SEO from '@/components/SEO';
import { AppDownloadSection } from '@/components/AppDownload';
import { HowItWorks, Testimonials, FAQSection } from '@/components/HomeSections';

import { toast } from 'sonner';
import { Input } from '@/components/ui/input';

const API = '/api';

// Newsletter Form Component
const NewsletterForm = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [subscribed, setSubscribed] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    
    setLoading(true);
    try {
      const response = await fetch(`${API}/newsletter/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        setSubscribed(true);
        toast.success(data.message || 'Bültenimize abone oldunuz!');
        setEmail('');
      } else {
        toast.error(data.detail || 'Bir hata oluştu');
      }
    } catch (error) {
      toast.error('Bağlantı hatası');
    }
    setLoading(false);
  };

  if (subscribed) {
    return (
      <div className="flex items-center justify-center gap-3 p-4 bg-green-500/20 rounded-xl border border-green-500/30">
        <CheckCircle className="w-6 h-6 text-green-400" />
        <span className="text-green-400 font-medium">Teşekkürler! Bültenimize abone oldunuz.</span>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
      <Input
        type="email"
        placeholder="Email adresiniz"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        className="flex-1 bg-white/10 border-white/20 text-white placeholder:text-white/50"
        required
      />
      <Button 
        type="submit" 
        disabled={loading}
        className="bg-primary hover:bg-primary/90 gap-2"
      >
        {loading ? (
          <RefreshCw className="w-4 h-4 animate-spin" />
        ) : (
          <>
            <Send className="w-4 h-4" />
            Abone Ol
          </>
        )}
      </Button>
    </form>
  );
};

// Hero slider images - Turkish baglama specific
const heroSlides = [
  {
    image: "https://static.prod-images.emergentagent.com/jobs/556aeb87-9456-4aa7-9cce-4caa3b24165a/images/5010764a33ffedd36611e389c97c62a5bd2257460b324b9c308d32497fef6107.png",
    title: "Bağlama Öğrenmenin",
    highlight: "En Kolay Yolu",
    description: "200+ video ders ile profesyonel eğitmenlerden öğrenin",
    cta: "premium"
  },
  {
    image: "https://static.prod-images.emergentagent.com/jobs/556aeb87-9456-4aa7-9cce-4caa3b24165a/images/b8adc86b7e535935658a6132e72f9ac61bb6897dae1e5eef41f169cdaa9b0e9f.png",
    title: "Temel ve İleri Düzey",
    highlight: "Video Eğitimler",
    description: "Başlangıçtan ustalığa, adım adım bağlama dersleri",
    cta: "premium"
  },
  {
    image: "https://static.prod-images.emergentagent.com/jobs/556aeb87-9456-4aa7-9cce-4caa3b24165a/images/4b57a07911b55e949b23d4e22aba4617c13794069df585efb71e24daa1372d8a.png",
    title: "Ücretsiz Araçlarla",
    highlight: "Pratik Yapın",
    description: "Akort cihazı, metronom ve nota arşivi tamamen ücretsiz",
    cta: "tools"
  }
];

// Multi-language support
const translations = {
  tr: {
    tuner: "Akort",
    metronome: "Metronom",
    notes: "Notalar",
    lessons: "Video Dersler",
    blog: "Blog",
    pricing: "Fiyatlar",
    login: "Giriş Yap",
    start: "Hemen Başla",
    myPanel: "Panelim",
    freeTools: "Ücretsiz Araçlar",
    videoLessons: "Premium Video Dersler",
    noteArchive: "Nota Arşivi",
    tunerDesc: "Ücretsiz browser tabanlı bağlama akort uygulaması",
    metronomeDesc: "Tempo çalışmalarınız için profesyonel metronom",
    noteArchiveDesc: "PDF formatında nota koleksiyonu",
    videoLessonsDesc: "A'dan Z'ye bağlama eğitimi",
    badge: "Türkiye'nin Online Bağlama Okulu",
    stats: { videos: "Video Ders", notes: "Nota Arşivi", students: "Öğrenci", experience: "Yıl Tecrübe" },
    wizard: "Saz Asistanı",
    wizardDesc: "AI destekli bağlama satın alma rehberi",
    premiumStart: "Premium Derslere Başla",
    freeToolsBtn: "Ücretsiz Araçları Keşfet"
  },
  en: {
    tuner: "Tuner",
    metronome: "Metronome", 
    notes: "Notes",
    lessons: "Video Lessons",
    blog: "Blog",
    pricing: "Pricing",
    login: "Login",
    start: "Get Started",
    myPanel: "Dashboard",
    freeTools: "Free Tools",
    videoLessons: "Premium Video Lessons",
    noteArchive: "Sheet Music",
    tunerDesc: "Free browser-based bağlama tuner",
    metronomeDesc: "Professional metronome for tempo practice",
    noteArchiveDesc: "PDF sheet music collection",
    videoLessonsDesc: "Complete bağlama education",
    badge: "Turkey's Online Bağlama School",
    stats: { videos: "Video Lessons", notes: "Sheet Music", students: "Students", experience: "Years Experience" },
    wizard: "Saz Assistant",
    wizardDesc: "AI-powered bağlama buying guide",
    premiumStart: "Start Premium Lessons",
    freeToolsBtn: "Explore Free Tools"
  },
  de: {
    tuner: "Stimmgerät",
    metronome: "Metronom",
    notes: "Noten",
    lessons: "Video-Kurse",
    blog: "Blog",
    pricing: "Preise",
    login: "Anmelden",
    start: "Loslegen",
    myPanel: "Dashboard",
    freeTools: "Kostenlose Tools",
    videoLessons: "Premium Video-Kurse",
    noteArchive: "Notenarchiv",
    tunerDesc: "Kostenloses Browser-Stimmgerät",
    metronomeDesc: "Professionelles Metronom",
    noteArchiveDesc: "PDF-Notensammlung",
    videoLessonsDesc: "Komplette Bağlama-Ausbildung",
    badge: "Türkeis Online Bağlama Schule",
    stats: { videos: "Video-Lektionen", notes: "Notenarchiv", students: "Studenten", experience: "Jahre Erfahrung" },
    wizard: "Saz-Assistent",
    wizardDesc: "KI-gestützter Kaufberater",
    premiumStart: "Premium-Kurse starten",
    freeToolsBtn: "Kostenlose Tools entdecken"
  },
  es: {
    tuner: "Afinador",
    metronome: "Metrónomo",
    notes: "Partituras",
    lessons: "Video Clases",
    blog: "Blog",
    pricing: "Precios",
    login: "Iniciar Sesión",
    start: "Empezar",
    myPanel: "Panel",
    freeTools: "Herramientas Gratuitas",
    videoLessons: "Video Clases Premium",
    noteArchive: "Archivo de Notas",
    tunerDesc: "Afinador gratuito para bağlama",
    metronomeDesc: "Metrónomo profesional",
    noteArchiveDesc: "Colección de partituras PDF",
    videoLessonsDesc: "Educación completa de bağlama",
    badge: "Escuela Online de Bağlama de Turquía",
    stats: { videos: "Video Clases", notes: "Partituras", students: "Estudiantes", experience: "Años Experiencia" },
    wizard: "Asistente Saz",
    wizardDesc: "Guía de compra con IA",
    premiumStart: "Iniciar Clases Premium",
    freeToolsBtn: "Explorar Herramientas"
  }
};

// Hero'da yüzen 3D nota animasyonu
const FLOATING_NOTES = [
  { glyph: '\u266A', left: '6%', top: '20%', size: 'text-4xl md:text-6xl', dur: '9s', delay: '0s' },
  { glyph: '\u266B', left: '14%', top: '62%', size: 'text-3xl md:text-5xl', dur: '11s', delay: '1.2s' },
  { glyph: '\u266C', left: '85%', top: '25%', size: 'text-4xl md:text-6xl', dur: '10s', delay: '0.6s' },
  { glyph: '\u266A', left: '90%', top: '58%', size: 'text-3xl md:text-5xl', dur: '12s', delay: '2s' },
  { glyph: '\u266B', left: '22%', top: '32%', size: 'text-5xl md:text-7xl', dur: '13s', delay: '0.9s', desktopOnly: true },
  { glyph: '\u266B', left: '75%', top: '72%', size: 'text-4xl md:text-6xl', dur: '10.5s', delay: '1.6s', desktopOnly: true },
  { glyph: '\u266A', left: '68%', top: '12%', size: 'text-2xl md:text-4xl', dur: '8.5s', delay: '2.4s' },
  { glyph: '\u266C', left: '35%', top: '78%', size: 'text-3xl md:text-4xl', dur: '11.5s', delay: '0.3s', desktopOnly: true },
];

const FloatingNotes = () => (
  <div
    className="absolute inset-0 z-10 pointer-events-none overflow-hidden"
    style={{ perspective: '800px' }}
    aria-hidden="true"
    data-testid="hero-floating-notes"
  >
    {FLOATING_NOTES.map((n, i) => (
      <span
        key={i}
        className={`floating-note absolute ${n.size} ${n.desktopOnly ? 'hidden md:block' : ''}`}
        style={{ left: n.left, top: n.top, animationDuration: n.dur, animationDelay: n.delay }}
      >
        {n.glyph}
      </span>
    ))}
  </div>
);

const LandingPage = () => {
  const [user, setUser] = useState(null);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [lang, setLang] = useState('tr');
  const [blogPosts, setBlogPosts] = useState([]);
  const [blogLoaded, setBlogLoaded] = useState(false);
  const navigate = useNavigate();
  const t = translations[lang];

  // Lazy load blog posts - only when scrolled into view
  useEffect(() => {
    if (blogLoaded) return;
    
    const observer = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && !blogLoaded) {
        setBlogLoaded(true);
        fetch(`${API}/blog?limit=3`)
          .then(res => res.ok ? res.json() : [])
          .then(posts => setBlogPosts(posts))
          .catch(() => {});
      }
    }, { rootMargin: '200px' });
    
    const blogSection = document.getElementById('blog-section');
    if (blogSection) observer.observe(blogSection);
    
    return () => observer.disconnect();
  }, [blogLoaded]);

  // Auto-slide effect
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  // Lazy auth check - after initial render
  useEffect(() => {
    const timer = setTimeout(() => {
      fetch(`${API}/auth/me`, { credentials: 'include' })
        .then(res => res.ok ? res.json() : null)
        .then(userData => userData && setUser(userData))
        .catch(() => {});
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  const nextSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
  }, []);

  const prevSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev - 1 + heroSlides.length) % heroSlides.length);
  }, []);

  const features = [
    {
      icon: <Music className="w-8 h-8" />,
      title: t.tuner,
      description: t.tunerDesc,
      link: "/baglama-akort",
      free: true
    },
    {
      icon: <Gauge className="w-8 h-8" />,
      title: t.metronome,
      description: t.metronomeDesc,
      link: "/metronome",
      free: true
    },
    {
      icon: <FileText className="w-8 h-8" />,
      title: t.noteArchive,
      description: t.noteArchiveDesc,
      link: "/turku-notalari",
      free: true
    },
    {
      icon: <Play className="w-8 h-8" />,
      title: t.videoLessons,
      description: t.videoLessonsDesc,
      link: "/baglama-dersleri",
      free: false
    },
    {
      icon: <Sparkles className="w-8 h-8" />,
      title: t.wizard,
      description: t.wizardDesc,
      link: "/satin-al",
      free: true,
      highlight: true
    }
  ];

  const [plans, setPlans] = useState([
    { id: "1_month", name: "1 Aylık", price: 299, popular: false },
    { id: "3_months", name: "3 Aylık", price: 699, popular: true },
    { id: "6_months", name: "6 Aylık", price: 1199, popular: false },
  ]);

  // Fetch dynamic pricing
  useEffect(() => {
    const fetchPricing = async () => {
      try {
        const response = await fetch(`${API}/pricing`);
        if (response.ok) {
          const data = await response.json();
          if (data.plans) {
            const planOrder = ['1_month', '3_months', '6_months'];
            const fetchedPlans = planOrder.map(planId => ({
              id: planId,
              name: data.plans[planId]?.name || planId,
              price: data.plans[planId]?.price || 0,
              popular: data.plans[planId]?.popular || false,
            })).filter(p => p.price > 0);
            if (fetchedPlans.length > 0) {
              setPlans(fetchedPlans);
            }
          }
        }
      } catch (error) {
        console.error('Error fetching pricing:', error);
      }
    };
    fetchPricing();
  }, []);

  const stats = [
    { value: "200+", label: t.stats.videos },
    { value: "50+", label: t.stats.notes },
    { value: "1000+", label: t.stats.students },
    { value: "5", label: t.stats.experience },
  ];

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      <SEO 
        title="Online Bağlama Dersleri | Türk Halk Müziği Eğitimi"
        description="Bağlama Satın Al ile online bağlama öğrenin. 200+ video ders, profesyonel eğitmenler, akort aleti, metronom ve nota arşivi. Yapay zeka destekli bağlama satın alma danışmanı."
        keywords="online bağlama dersi, saz öğren, bağlama kursu, türk halk müziği eğitimi, bağlama videoları, bağlama akort, saz metronom, nota arşivi, dersjet"
        canonical="/"
      />
      
      {/* Navigation */}
      <MainNavigation lang={lang} setLang={setLang} user={user} />

      {/* Hero Section with Slider */}
      <section className="relative min-h-[100svh] flex items-center justify-center pt-20 pb-40 md:pb-32 overflow-hidden">
        {/* Background Slides - Only render active and next slide for performance */}
        {heroSlides.map((slide, index) => {
          const isActive = index === currentSlide;
          const isNext = index === (currentSlide + 1) % heroSlides.length;
          if (!isActive && !isNext) return null;
          
          return (
            <div
              key={index}
              className={`absolute inset-0 transition-opacity duration-1000 ${
                isActive ? 'opacity-100' : 'opacity-0'
              }`}
            >
              <div 
                className="absolute inset-0 scale-105"
                style={{
                  backgroundImage: `url('${slide.image}')`,
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                  transform: isActive ? 'scale(1.05)' : 'scale(1)',
                  transition: 'transform 6s ease-out'
                }}
              />
              <div className="absolute inset-0 bg-black/60" />
            </div>
          );
        })}
        
        {/* Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#0A0A0A]/50 to-[#0A0A0A] z-10" />

        {/* 3D Floating Musical Notes */}
        <FloatingNotes />
        
        {/* Slide Navigation Arrows */}
        <button 
          onClick={prevSlide}
          className="absolute left-4 top-1/2 -translate-y-1/2 z-20 p-3 rounded-full glass hover:bg-white/20 transition-all hidden md:block"
        >
          <ChevronLeftIcon className="w-6 h-6" />
        </button>
        <button 
          onClick={nextSlide}
          className="absolute right-4 top-1/2 -translate-y-1/2 z-20 p-3 rounded-full glass hover:bg-white/20 transition-all hidden md:block"
        >
          <ChevronRight className="w-6 h-6" />
        </button>

        {/* Slide Indicators */}
        <div className="absolute bottom-24 md:bottom-16 left-1/2 -translate-x-1/2 z-20 flex gap-2">
          {heroSlides.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentSlide(index)}
              className={`w-2 h-2 rounded-full transition-all ${
                index === currentSlide ? 'w-8 bg-primary' : 'bg-white/40 hover:bg-white/60'
              }`}
            />
          ))}
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="animate-fade-in-up">
            <span className="inline-block px-3 py-1.5 md:px-4 md:py-2 rounded-full bg-primary/20 text-primary text-xs md:text-sm font-medium mb-4 md:mb-6">
              🎵 {t.badge}
            </span>
          </div>
          
          {/* Dynamic Title based on slide */}
          <h1
            key={currentSlide}
            className="font-heading text-3xl sm:text-4xl md:text-7xl font-bold tracking-tight mb-3 md:mb-6 animate-title-3d"
          >
            {heroSlides[currentSlide].title}<br />
            <span className="gradient-text">{heroSlides[currentSlide].highlight}</span>
          </h1>
          
          <p className="text-base md:text-2xl text-muted-foreground max-w-3xl mx-auto mb-6 md:mb-10 animate-fade-in-up delay-200">
            {heroSlides[currentSlide].description}
          </p>

          {/* Dynamic CTAs based on slide */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 md:gap-4 animate-fade-in-up delay-300">
            {heroSlides[currentSlide].cta === 'premium' ? (
              <>
                <Button 
                  onClick={() => navigate('/baglama-dersleri')}
                  size="lg"
                  className="bg-primary hover:bg-primary/90 text-white rounded-full px-6 py-4 md:px-8 md:py-6 text-base md:text-lg shadow-[0_0_30px_rgba(255,85,0,0.4)] hover:shadow-[0_0_40px_rgba(255,85,0,0.6)] transition-all w-full sm:w-auto"
                >
                  <Crown className="mr-2 w-4 h-4 md:w-5 md:h-5" />
                  {t.premiumStart}
                </Button>
                <Button 
                  variant="outline"
                  size="lg"
                  onClick={() => navigate('/fiyatlar')}
                  className="border-white/30 text-white hover:bg-white/10 rounded-full px-6 py-4 md:px-8 md:py-6 text-base md:text-lg w-full sm:w-auto"
                >
                  {t.pricing}
                  <ChevronRight className="ml-2 w-4 h-4 md:w-5 md:h-5" />
                </Button>
              </>
            ) : (
              <>
                <Button 
                  onClick={() => navigate('/baglama-akort')}
                  size="lg"
                  className="bg-primary hover:bg-primary/90 text-white rounded-full px-6 py-4 md:px-8 md:py-6 text-base md:text-lg shadow-[0_0_30px_rgba(255,85,0,0.4)] hover:shadow-[0_0_40px_rgba(255,85,0,0.6)] transition-all w-full sm:w-auto"
                >
                  {t.freeToolsBtn}
                  <ChevronRight className="ml-2 w-4 h-4 md:w-5 md:h-5" />
                </Button>
                <Button 
                  variant="outline"
                  size="lg"
                  onClick={() => navigate('/baglama-dersleri')}
                  className="border-white/30 text-white hover:bg-white/10 rounded-full px-6 py-4 md:px-8 md:py-6 text-base md:text-lg w-full sm:w-auto"
                >
                  <Crown className="mr-2 w-4 h-4 md:w-5 md:h-5" />
                  {t.premiumStart}
                </Button>
              </>
            )}
          </div>

          {/* Trust Badges */}
          <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 mt-6 md:mt-8 animate-fade-in-up delay-300 mb-16 md:mb-0" data-testid="hero-trust-badges">
            <span className="flex items-center gap-1.5 text-xs md:text-sm text-white/70">
              <CheckCircle className="w-4 h-4 text-green-400" /> İlk 2 ders ücretsiz
            </span>
            <span className="flex items-center gap-1.5 text-xs md:text-sm text-white/70">
              <CheckCircle className="w-4 h-4 text-green-400" /> İstediğin zaman iptal
            </span>
            <span className="flex items-center gap-1.5 text-xs md:text-sm text-white/70">
              <Users className="w-4 h-4 text-primary" /> 1000+ öğrenci
            </span>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 relative z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl md:text-4xl font-bold mb-4">
              {t.freeTools} <span className="gradient-text">& {t.videoLessons}</span>
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              {lang === 'tr' ? 'Akort cihazı, metronom ve nota arşivi ücretsiz kullanımınıza sunulmuştur.' : 'Free tuner, metronome and sheet music archive available for you.'}
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-6">
            {features.map((feature, index) => (
              <Link
                key={index}
                to={feature.link}
                className={`group p-6 rounded-2xl transition-all duration-300 border ${
                  feature.highlight 
                    ? 'bg-gradient-to-br from-primary/20 to-orange-500/10 border-primary/50 hover:border-primary hover:shadow-[0_0_30px_rgba(255,85,0,0.3)]' 
                    : 'glass border-white/10 hover:border-primary/50'
                } hover:transform hover:scale-105`}
              >
                <div className={`w-14 h-14 rounded-xl flex items-center justify-center mb-4 ${
                  feature.highlight ? 'bg-primary' : 'bg-primary/20'
                } group-hover:bg-primary transition-colors`}>
                  {feature.icon}
                </div>
                <h3 className="font-heading text-xl font-semibold mb-2">{feature.title}</h3>
                <p className="text-muted-foreground text-sm mb-4">{feature.description}</p>
                <span className={`inline-block px-3 py-1 rounded-full text-xs ${
                  feature.free ? 'bg-green-500/20 text-green-400' : 'bg-primary/20 text-primary'
                }`}>
                  {feature.free ? (lang === 'tr' ? 'Ücretsiz' : 'Free') : 'Premium'}
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <HowItWorks />

      {/* Stats Section */}
      <section className="py-16 glass">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="font-heading text-4xl md:text-5xl font-bold gradient-text mb-2">
                  {stat.value}
                </div>
                <div className="text-muted-foreground">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <Testimonials />

      {/* Blog Preview Section */}
      <section id="blog-section" className="py-24 bg-gradient-to-b from-transparent via-primary/5 to-transparent">
        {blogPosts.length > 0 ? (
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/20 text-primary text-sm mb-4">
                <BookOpen className="w-4 h-4" />
                Blog & Rehberler
              </div>
              <h2 className="font-heading text-3xl md:text-4xl font-bold mb-4">
                Bağlama Hakkında <span className="gradient-text">Her Şey</span>
              </h2>
              <p className="text-muted-foreground max-w-2xl mx-auto">
                Bağlama dersleri, teknikler, bakım ipuçları ve daha fazlası. Uzman içerikleriyle bağlama yolculuğunuzda yanınızdayız.
              </p>
            </div>

            {/* Blog Grid */}
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
              {blogPosts.slice(0, 6).map((post, index) => (
                <Link
                  key={post.slug}
                  to={`/blog/${post.slug}`}
                  className="group block"
                >
                  <div className="glass rounded-2xl overflow-hidden border border-white/10 hover:border-primary/50 transition-all duration-300 h-full">
                    {/* Image */}
                    <div className="relative aspect-video overflow-hidden">
                      <img 
                        src={post.image} 
                        alt={post.title}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                        loading="lazy"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                      <div className="absolute bottom-3 left-3 flex items-center gap-2">
                        <span className="px-2 py-1 bg-primary/80 backdrop-blur-sm rounded-full text-xs font-medium text-white">
                          {post.category === 'teknik' ? 'Teknik' : 
                           post.category === 'rehber' ? 'Rehber' : 
                           post.category === 'kultur' ? 'Kültür' : 
                           post.category === 'ekipman' ? 'Ekipman' : 'Genel'}
                        </span>
                        <span className="flex items-center gap-1 text-xs text-white/80">
                          <Clock className="w-3 h-3" />
                          {post.read_time} dk
                        </span>
                      </div>
                    </div>
                    {/* Content */}
                    <div className="p-5">
                      <h3 className="font-heading text-lg font-semibold mb-2 group-hover:text-primary transition-colors line-clamp-2">
                        {post.title}
                      </h3>
                      <p className="text-sm text-muted-foreground line-clamp-2">
                        {post.description}
                      </p>
                    </div>
                  </div>
                </Link>
              ))}
            </div>

            {/* CTA Button */}
            <div className="text-center">
              <Link to="/blog">
                <Button size="lg" variant="outline" className="rounded-full px-8 gap-2 border-primary/50 hover:bg-primary/10">
                  Tüm Yazıları Gör
                  <ArrowRight className="w-4 h-4" />
                </Button>
              </Link>
            </div>
          </div>
        ) : (
          <div className="max-w-7xl mx-auto px-4 text-center">
            <div className="animate-pulse h-64 bg-white/5 rounded-xl"></div>
          </div>
        )}
      </section>

      {/* Pricing Preview */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl md:text-4xl font-bold mb-4">
              {lang === 'tr' ? 'Abonelik Planları' : 'Subscription Plans'}
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              {lang === 'tr' ? 'Size en uygun planı seçin ve bağlama yolculuğunuza başlayın.' : 'Choose the plan that suits you best.'}
            </p>
          </div>

          {/* 3 Plan Kartı */}
          <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto mb-10">
            {plans.map((plan) => (
              <div
                key={plan.id}
                className={`relative p-6 rounded-2xl ${
                  plan.popular
                    ? 'glass border-2 border-primary transform md:scale-105'
                    : 'glass border border-white/10'
                }`}
              >
                {plan.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 bg-primary rounded-full text-sm font-medium">
                    {lang === 'tr' ? 'En Popüler' : 'Most Popular'}
                  </div>
                )}
                <div className="text-center">
                  <h3 className="font-heading text-xl font-semibold mb-2">{plan.name}</h3>
                  <div className="mb-6">
                    <span className="text-4xl font-bold">₺{plan.price}</span>
                  </div>
                  <ul className="space-y-3 text-left mb-6">
                    <li className="flex items-center gap-2 text-sm">
                      <CheckCircle className="w-4 h-4 text-green-400 flex-shrink-0" />
                      <span>{lang === 'tr' ? 'Tüm video derslere erişim' : 'Access to all video lessons'}</span>
                    </li>
                    <li className="flex items-center gap-2 text-sm">
                      <CheckCircle className="w-4 h-4 text-green-400 flex-shrink-0" />
                      <span>{lang === 'tr' ? 'PDF nota arşivi' : 'PDF sheet music archive'}</span>
                    </li>
                    <li className="flex items-center gap-2 text-sm">
                      <CheckCircle className="w-4 h-4 text-green-400 flex-shrink-0" />
                      <span>{lang === 'tr' ? 'İlerleme takibi' : 'Progress tracking'}</span>
                    </li>
                  </ul>
                  <Button
                    onClick={() => navigate(`/fiyatlar?plan=${plan.id}`)}
                    className={`w-full rounded-full ${
                      plan.popular
                        ? 'bg-primary hover:bg-primary/90'
                        : 'bg-white/10 hover:bg-primary/80 border border-white/20'
                    }`}
                    data-testid={`plan-start-btn-${plan.id}`}
                  >
                    {lang === 'tr' ? 'Hemen Başla' : 'Get Started'}
                  </Button>
                </div>
              </div>
            ))}
          </div>

          {/* Tek Buton */}
          <div className="text-center">
            <Button
              onClick={() => navigate('/fiyatlar')}
              size="lg"
              className="rounded-full bg-primary hover:bg-primary/90 text-lg px-12 py-6 shadow-[0_0_30px_rgba(255,85,0,0.3)]"
              data-testid="view-pricing-btn"
            >
              {lang === 'tr' ? 'Fiyatları Gör' : 'View Pricing'}
            </Button>
          </div>
        </div>
      </section>

      {/* DersJet App Download */}
      <AppDownloadSection />

      {/* FAQ */}
      <FAQSection />

      {/* Newsletter Section */}
      <section className="py-16 bg-gradient-to-r from-primary/20 via-primary/10 to-primary/20">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/20 text-primary text-sm mb-4">
            <Mail className="w-4 h-4" />
            Bülten
          </div>
          <h2 className="font-heading text-2xl md:text-3xl font-bold mb-4">
            Bağlama Dünyasından Haberdar Olun
          </h2>
          <p className="text-muted-foreground mb-8">
            Yeni dersler, indirimler ve bağlama satın alma tavsiyeleri için bültenimize abone olun. Spam yapmıyoruz!
          </p>
          <NewsletterForm />
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center">
                  <Music className="w-5 h-5 text-white" />
                </div>
                <span className="font-heading text-xl font-bold">Bağlama Satın Al</span>
              </div>
              <p className="text-sm text-muted-foreground">
                {t.badge}
              </p>
            </div>
            <div>
              <h4 className="font-semibold mb-4">{t.freeTools}</h4>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li><Link to="/baglama-akort" className="hover:text-white">{t.tuner}</Link></li>
                <li><Link to="/metronome" className="hover:text-white">{t.metronome}</Link></li>
                <li><Link to="/turku-notalari" className="hover:text-white">{t.notes}</Link></li>
                <li><Link to="/satin-al" className="hover:text-white">{t.wizard}</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Premium</h4>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li><Link to="/baglama-dersleri" className="hover:text-white">{t.lessons}</Link></li>
                <li><Link to="/fiyatlar" className="hover:text-white">{t.pricing}</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">{lang === 'tr' ? 'Yasal' : 'Legal'}</h4>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li><Link to="/legal/gizlilik" className="hover:text-white">{lang === 'tr' ? 'Gizlilik Politikası' : 'Privacy Policy'}</Link></li>
                <li><Link to="/legal/kullanim" className="hover:text-white">{lang === 'tr' ? 'Kullanım Şartları' : 'Terms of Use'}</Link></li>
                <li><Link to="/legal/kvkk" className="hover:text-white">KVKK</Link></li>
              </ul>
            </div>
          </div>
          <div className="mt-8 pt-8 border-t border-white/10 text-center text-sm text-muted-foreground">
            © 2026 Bağlama Satın Al - {t.badge}
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
